// Utility for loading/saving OpenAI API key from adminSettings collection
import { getAdminSettings, setAdminSettings, deleteAdminSettings } from '@/lib/collections/adminSettings';
import { getSiteSettings } from '@/lib/collections/siteSettings';
import { Time } from '@/lib/db-client';
import { BASE_WALLPAPER_CATEGORIES } from '@/utils/wallpaperCategories';

export async function loadApiKey(): Promise<string> {
  try {
    const result = await getAdminSettings('openaiKey');
    return result?.value ?? '';
  } catch {
    return '';
  }
}

export async function saveApiKey(key: string): Promise<void> {
  if (!key.trim()) return;
  try {
    await setAdminSettings('openaiKey', {
      value: key.trim(),
      updatedAt: Time.Now,
    });
  } catch (err) {
    console.error('Failed to save API key:', err);
  }
}

export async function deleteApiKey(): Promise<boolean> {
  try {
    return await deleteAdminSettings('openaiKey');
  } catch (err) {
    console.error('Failed to delete API key:', err);
    return false;
  }
}

/**
 * Convert a File to a base64 data URL for use with OpenAI Vision API.
 */
export async function fileToBase64DataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Call OpenAI Vision API to analyze a wallpaper image and suggest title + category.
 * Returns null if the call fails or key is invalid.
 * imageUrl can be a hosted URL or a base64 data URL.
 */
export async function analyzeWallpaperImage(imageUrl: string, apiKey: string): Promise<{
  title: string;
  category: string;
} | null> {
  if (!apiKey.trim() || !imageUrl) return null;

  // Load the admin-managed category list from the DB; fall back to base list if unavailable.
  let VALID_CATEGORIES: string[] = BASE_WALLPAPER_CATEGORIES;
  try {
    const doc = await getSiteSettings('wallpaperCategories');
    if (doc?.value) {
      const parsed = JSON.parse(doc.value) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        VALID_CATEGORIES = parsed as string[];
      }
    }
  } catch {
    // Fall back to base list on any error
  }

  const prompt = `Analyze this wallpaper image. Return JSON: { "title": string (2-4 words, evocative name for this wallpaper), "category": string (one of: ${VALID_CATEGORIES.join(', ')}) }`;

  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey.trim()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      max_tokens: 100,
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'image_url',
              image_url: { url: imageUrl, detail: 'low' },
            },
            {
              type: 'text',
              text: prompt,
            },
          ],
        },
      ],
      response_format: { type: 'json_object' },
    }),
  });

  if (!response.ok) {
    throw new Error(`OpenAI API error: ${response.status}`);
  }

  const data = await response.json() as {
    choices: Array<{ message: { content: string } }>;
  };

  const content = data.choices?.[0]?.message?.content;
  if (!content) return null;

  const parsed = JSON.parse(content) as { title?: string; category?: string };
  if (!parsed.title && !parsed.category) return null;

  // Normalize category to one of the known ones
  const cat = parsed.category ?? '';
  const matched = VALID_CATEGORIES.find(c => c.toLowerCase() === cat.toLowerCase()) ?? cat;

  return {
    title: parsed.title ?? '',
    category: matched,
  };
}
